import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { Button, VARIATION } from '@zocdoc-frontend-common/components-button';
import { Checkbox } from '@zocdoc-frontend-common/components-checkbox';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { Link } from '@zocdoc-frontend-common/components-link';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import {
    blue70,
    blue100,
    negativeMedium,
    slate30,
} from '@zocdoc-mezzanine/foundations-colors';
import { labelSmall } from '@zocdoc-mezzanine/foundations-typography';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';
import {
    BASE_EXPIRED_PASSWORD_REDIRECT,
    GaActionText,
    TRACKING_ELEMENTS,
} from './constants';
import * as SignInErrors from './SignInErrors';

const Form = styled.form.attrs({
    noValidate: 'novalidate',
})`
    margin-bottom: 28px;
`;

const Header = styled.h2`
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    color: ${blue100};
    margin-bottom: 16px;
`;

const InputLabel = styled.div`
    ${labelSmall};
    color: ${blue100};
    margin-bottom: 4px;
    text-align: left;

    display: ${(p) => (p.showFieldLabel ? 'block' : 'none')};
`;

const InputContainer = styled.label`
    display: block;
    margin-bottom: 8px;

    ${breakpoint.small} {
        margin-bottom: 12px;
    }
`;

const SignIn = styled.div`
    margin-top: 16px;

    ${breakpoint.small} {
        margin-top: 24px;
    }
`;

const ForgotPassword = styled.div`
    clear: both;
    margin: 20px 0;
`;

const ForgotPasswordLink = styled(Link)`
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    color: ${blue70};
    border-bottom: 0;
`;

const LockedResetPasswordContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: left;
    align-items: left;

    ${breakpoint.small} {
        flex-direction: ${(p) => (p.useModal ? 'column' : 'row')};
    }
`;

const LockedMessage = styled.div`
    display: inline-block;
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    color: ${negativeMedium};
    padding-right: 5px;
`;

const StyledTextbox = styled(Textbox)`
    &:focus::placeholder {
        color: ${slate30};
    }
`;

export const StyledLoginButton = styled(Button)`
    ${(p) =>
        p.$showTransparentButton &&
        `background-color: transparent; border: 1px solid ${blue100};`};
    font-size: 15px;
    line-height: 18px;
    padding: 11px 0;
`;

const CheckboxLabel = styled.div`
    color: ${blue100};
    font: 15px/1.333 ${FontFamily.SharpSansMedium};
    padding-top: 1px;
    padding-bottom: 3px;
    white-space: nowrap;
`;

const FlushCheckbox = styled(Checkbox)`
    padding-left: 0;
`;

const ErrorMessageContainer = styled(ErrorMessage)`
    color: ${negativeMedium};
    ${(p) =>
        p.$isInMfaLoginExperiment &&
        `display: flex; justify-content: flex-start;`};
`;

class BaseSignInFormView extends Component {
    static propTypes = {
        autoFocusEmailField: PropTypes.bool,
        buttonStyle: PropTypes.string,
        defaultEmailValue: PropTypes.string,
        emailValidationError: PropTypes.string,
        fetchUserOnSignIn: PropTypes.bool,
        forgotPasswordLinkTarget: PropTypes.string,
        isInMfaLoginExperiment: PropTypes.bool,
        isInMultiStepSignIn: PropTypes.bool,
        isPatientMode: PropTypes.bool,
        onChangeEmailInput: PropTypes.func,
        onChangePasswordInput: PropTypes.func,
        onChangeRememberMeCheckbox: PropTypes.func,
        onSubmitSignIn: PropTypes.func,
        onSubmitSignInWithMFA: PropTypes.func,
        onSubmitSignInWithUserFetch: PropTypes.func,
        passwordExpired: PropTypes.bool,
        passwordValidationError: PropTypes.string,
        recordRememberMeAssignment: PropTypes.func,
        returnUrl: PropTypes.string,
        rememberMe: PropTypes.bool,
        sendMetric: PropTypes.func,
        showFacebookLogin: PropTypes.bool,
        showFieldLabel: PropTypes.bool,
        showHeader: PropTypes.bool,
        showRememberMe: PropTypes.bool,
        signInError: PropTypes.string,
        signingIn: PropTypes.bool,
        socialLoginValidationError: PropTypes.number,
        trackClickForgotPassword: PropTypes.func,
        trackClickResetPassword: PropTypes.func,
        trackEmailFieldSelect: PropTypes.func,
        trackPasswordExpired: PropTypes.func,
        trackPasswordFieldSelect: PropTypes.func,
        trackShowLockedError: PropTypes.func,
        trackShowLockedResetPassword: PropTypes.func,
        useModal: PropTypes.bool,
        validationError: PropTypes.oneOf(Object.values(SignInErrors)),
    };

    static defaultProps = {
        autoFocusEmailField: false,
        buttonStyle: VARIATION.PRIMARY,
        defaultEmailValue: '',
        fetchUserOnSignIn: false,
        forgotPasswordLinkTarget: '_self',
        isInMfaLoginExperiment: false,
        isInMultiStepSignIn: false,
        isPatientMode: true,
        onChangeEmailInput: noop,
        onChangePasswordInput: noop,
        onChangeRememberMeCheckbox: noop,
        onSubmitSignIn: noop,
        onSubmitSignInWithMFA: noop,
        onSubmitSignInWithUserFetch: noop,
        passwordExpired: false,
        recordRememberMeAssignment: noop,
        rememberMe: true,
        sendMetric: noop,
        showFieldLabel: false,
        showHeader: false,
        showRememberMe: false,
        signInError: '',
        signingIn: false,
        trackClickForgotPassword: noop,
        trackClickResetPassword: noop,
        trackEmailFieldSelect: noop,
        trackPasswordExpired: noop,
        trackPasswordFieldSelect: noop,
        trackShowLockedError: noop,
        trackShowLockedResetPassword: noop,
        useModal: false,
    };

    state = {
        // To guarantee that we only track it once
        shouldTrackShowLockedMessage: true,
    };

    componentDidMount() {
        this.props.recordRememberMeAssignment();
        this.handleDefaultEmail();
    }

    render() {
        const {
            passwordExpired,
            returnUrl,
            sendMetric,
            showFieldLabel,
            defaultEmailValue,
            trackPasswordExpired,
            isInMultiStepSignIn,
            ...props
        } = this.props;

        if (passwordExpired) {
            trackPasswordExpired();
            sendMetric({
                action: GaActionText.trackPasswordExpired,
                element: TRACKING_ELEMENTS.passwordExpired,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
            const redirectUrl = returnUrl
                ? `${BASE_EXPIRED_PASSWORD_REDIRECT}&returnUrl=${returnUrl}`
                : BASE_EXPIRED_PASSWORD_REDIRECT;
            window.location.assign(redirectUrl);
            return <div />;
        }

        return (
            <Form
                action="/signin?html=1"
                method="post"
                data-test="sign-in-form"
                {...filterToValidAttributes(props)}
                onSubmit={this.handleSubmit}
            >
                {this.maybeRenderHeader()}
                <InputContainer data-test="email-input-container">
                    <InputLabel
                        data-test="sign-in-email-label"
                        showFieldLabel={showFieldLabel}
                    >
                        {'Email address'}
                    </InputLabel>
                    <StyledTextbox
                        className="email-input exclude-from-playback"
                        data-test="email-input"
                        data-uem-id="email-input"
                        focusOnMount={this.props.autoFocusEmailField}
                        invalidMessage={this.props.emailValidationError}
                        onChange={this.handleEmailChange}
                        onFocus={this.handleEmailFocus}
                        placeholder="Email address"
                        type="email"
                        defaultValue={defaultEmailValue || ''}
                        readOnly={isInMultiStepSignIn}
                        disabled={isInMultiStepSignIn}
                    />
                </InputContainer>

                <InputContainer data-test="password-input-container">
                    <InputLabel
                        data-test="sign-in-password-label"
                        showFieldLabel={showFieldLabel}
                    >
                        Password
                    </InputLabel>
                    <StyledTextbox
                        className="password-input exclude-from-playback"
                        data-test="password-input"
                        data-uem-id="password-input"
                        invalidMessage={this.props.passwordValidationError}
                        onChange={this.handlePasswordChange}
                        onFocus={this.handlePasswordFocus}
                        placeholder="Password"
                        type="password"
                    />
                </InputContainer>

                {this.maybeRenderRememberMe()}

                {this.maybeRenderValidationErrorParagraph()}

                <SignIn>
                    <StyledLoginButton
                        data-test="sign-in-form-submit"
                        data-uem-id="sign-in-form-submit"
                        disabled={this.props.signingIn}
                        fluid
                        onClick={this.handleSignInClick}
                        size="btn-med"
                        variation={this.props.buttonStyle}
                        $showTransparentButton={!isInMultiStepSignIn}
                    >
                        {isInMultiStepSignIn ? 'Continue' : 'Log in'}
                    </StyledLoginButton>
                </SignIn>
                {!isInMultiStepSignIn && (
                    <ForgotPassword>
                        <ForgotPasswordLink
                            beforeClick={this.handleForgotPasswordClick}
                            data-test="sign-in-forgot-password"
                            href="/auth/ForgotPassword"
                            target={this.props.forgotPasswordLinkTarget}
                        >
                            Forgot your password?
                        </ForgotPasswordLink>
                    </ForgotPassword>
                )}
            </Form>
        );
    }

    maybeRenderHeader() {
        if (this.props.showHeader) {
            return (
                <Header data-test="sign-in-header">
                    Log in to your Zocdoc account
                </Header>
            );
        }
    }

    maybeRenderRememberMe() {
        const { isPatientMode, rememberMe, showRememberMe } = this.props;
        if (isPatientMode && showRememberMe) {
            return (
                <InputContainer data-test="remember-me-checkbox-container">
                    <FlushCheckbox
                        checked={rememberMe}
                        data-test="remember-me-checkbox"
                        iconStyle={{ marginLeft: '0' }}
                        onChecked={this.handleRememberMeChange}
                    >
                        <CheckboxLabel>Keep me logged in</CheckboxLabel>
                    </FlushCheckbox>
                </InputContainer>
            );
        }
    }

    maybeRenderValidationErrorParagraph() {
        const {
            isInMfaLoginExperiment,
            signInError,
            validationError,
            sendMetric,
            socialLoginValidationError,
            showFacebookLogin,
            trackShowLockedError,
            trackShowLockedResetPassword,
        } = this.props;
        const { shouldTrackShowLockedMessage } = this.state;

        /* validationError will come from state, when validating details entered by a user
        validationErrors from v1 sign in are ints
        signInErrors from new v2 sign in w/ mfa are strings
        socialLoginValidationError will come from props, loaded from the url
        */
        let errorMessage;

        if (signInError) {
            errorMessage = signInError;
        } else {
            const error = validationError || socialLoginValidationError;

            if (error) {
                switch (error) {
                    case SignInErrors.AccountLockedForMisbehavior:
                        if (shouldTrackShowLockedMessage) {
                            trackShowLockedError();
                            sendMetric({
                                action: GaActionText.trackShowLockedError,
                                element: TRACKING_ELEMENTS.lockedErrorMessage,
                                initiator: EVENT_INITIATORS.USER,
                                interactionType: INTERACTION_TYPES.VIEW,
                            });
                            this.setState({
                                shouldTrackShowLockedMessage: false,
                            });
                        }
                        errorMessage = 'Sorry, this account has been locked.';
                        break;

                    case SignInErrors.AccountLockedTooManyAuthFailures:
                        if (shouldTrackShowLockedMessage) {
                            trackShowLockedResetPassword();
                            sendMetric({
                                action: GaActionText.trackShowLockedResetPassword,
                                element: TRACKING_ELEMENTS.lockedErrorMessage,
                                initiator: EVENT_INITIATORS.USER,
                                interactionType: INTERACTION_TYPES.VIEW,
                            });
                            this.setState({
                                shouldTrackShowLockedMessage: false,
                            });
                        }
                        return this.renderLockedResetPasswordMessage();

                    case SignInErrors.IncorrectEmailOrPassword:
                        errorMessage = 'Incorrect email or password.';
                        break;

                    case SignInErrors.SocialLoginOther:
                    case SignInErrors.TryAgain:
                        errorMessage =
                            'Something went wrong, please try again...';
                        break;

                    case SignInErrors.SocialLoginRequestExpired:
                        errorMessage = 'Signin timed out, please try again.';
                        break;

                    case SignInErrors.BlankEmail:
                        errorMessage = 'Please type an email.';
                        break;

                    case SignInErrors.InvalidEmail:
                        errorMessage = 'Please type a valid email.';
                        break;

                    case SignInErrors.BlankPassword:
                        errorMessage = 'Please type your password.';
                        break;

                    case SignInErrors.SocialLoginNonPatient:
                        errorMessage = showFacebookLogin
                            ? 'Only patients can log in with Facebook or Google. Please log in with your email instead.'
                            : 'Only patients can log in with Google. Please log in with your email instead.';
                        break;

                    default:
                        errorMessage = 'Something went wrong...';
                        break;
                }
            }
        }

        return (
            <ErrorMessageContainer
                data-test="validation-error-paragraph"
                $isInMfaLoginExperiment={isInMfaLoginExperiment}
            >
                {errorMessage}
            </ErrorMessageContainer>
        );
    }

    renderLockedResetPasswordMessage() {
        const { useModal } = this.props;

        return (
            <LockedResetPasswordContainer useModal={useModal}>
                <LockedMessage data-test="sign-in-locked-message">
                    You've attempted to log in too many times.
                </LockedMessage>
                <ForgotPasswordLink
                    beforeClick={this.handleResetPasswordClick}
                    data-test="sign-in-locked-reset-password"
                    href="/auth/ForgotPassword"
                >
                    Please reset your password.
                </ForgotPasswordLink>
            </LockedResetPasswordContainer>
        );
    }

    handleSubmit = (e) => {
        e.preventDefault();

        const {
            email,
            fetchUserOnSignIn,
            isInMfaLoginExperiment,
            isPatientMode,
            password,
            rememberMe,
            sendMetric,
            showRememberMe,
            onSubmitSignInWithUserFetch,
            onSubmitSignIn,
            onSubmitSignInWithMFA,
        } = this.props;

        const shouldRememberMe = rememberMe && isPatientMode && showRememberMe;
        sendMetric({
            action: GaActionText.trackClickContinue,
            element: TRACKING_ELEMENTS.logInButton,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });

        if (isInMfaLoginExperiment) {
            onSubmitSignInWithMFA(email, password);
        } else if (fetchUserOnSignIn) {
            onSubmitSignInWithUserFetch(email, password, shouldRememberMe);
        } else {
            onSubmitSignIn(email, password, shouldRememberMe);
        }
    };

    handleEmailChange = (e) => this.props.onChangeEmailInput(e.target.value);
    handlePasswordChange = (e) =>
        this.props.onChangePasswordInput(e.target.value);
    handleRememberMeChange = (e) => {
        const { sendMetric, onChangeRememberMeCheckbox } = this.props;
        sendMetric({
            action: GaActionText.trackRememberMeChange,
            element: TRACKING_ELEMENTS.rememberMeCheckbox,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        onChangeRememberMeCheckbox(e);
    };

    handleForgotPasswordClick = () => {
        const { sendMetric, trackClickForgotPassword } = this.props;
        trackClickForgotPassword();
        sendMetric({
            action: GaActionText.trackClickForgotPassword,
            element: TRACKING_ELEMENTS.forgotPasswordLink,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    handleResetPasswordClick = () => {
        const { sendMetric, trackClickResetPassword } = this.props;
        trackClickResetPassword();
        sendMetric({
            action: GaActionText.trackClickResetPassword,
            element: TRACKING_ELEMENTS.lockedResetPasswordLink,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    handleEmailFocus = () => {
        const { sendMetric, trackEmailFieldSelect } = this.props;
        trackEmailFieldSelect();
        sendMetric({
            action: GaActionText.trackEmailFieldSelect,
            element: TRACKING_ELEMENTS.emailTextbox,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.SELECT,
        });
    };

    handlePasswordFocus = () => {
        const { sendMetric, trackPasswordFieldSelect } = this.props;
        trackPasswordFieldSelect();
        sendMetric({
            action: GaActionText.trackPasswordFieldSelect,
            element: TRACKING_ELEMENTS.passwordTextbox,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.SELECT,
        });
    };

    handleDefaultEmail = () => {
        const { defaultEmailValue } = this.props;
        if (defaultEmailValue) {
            this.props.onChangeEmailInput(defaultEmailValue);
        }
    };
}

export const SignInFormView = BaseSignInFormView;
